"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "STEP 01",
  title: "INITIAL PROJECT BRIEF",
  description: "We start with a detailed discovery session to define your project requirements, technical specifications, and long-term goals. Our team evaluates your current infrastructure to ensure seamless integration.",
  timeframe: "WEEK 1-2"
}, {
  number: "STEP 02",
  title: "STRATEGIC ARCHITECTURE",
  description: "Our architects develop a comprehensive blueprint tailored to your operational needs. We focus on scalability, performance, and efficiency to build a robust foundation for your success.",
  timeframe: "WEEK 3-4"
}, {
  number: "STEP 03",
  title: "SYSTEM IMPLEMENTATION",
  description: "Our engineers execute the deployment phase with precision. We prioritize minimal downtime and rigorous quality control to ensure every component functions at peak performance.",
  timeframe: "WEEK 5-6"
}, {
  number: "STEP 04",
  title: "QUALITY ASSURANCE",
  description: "We conduct thorough testing and validation of all systems. Our team verifies that every deliverable meets our high standards for reliability, security, and user experience.",
  timeframe: "WEEK 7-8"
}, {
  number: "STEP 05",
  title: "ONGOING OPTIMIZATION",
  description: "We provide continuous monitoring and support to keep your systems running smoothly. Our team is dedicated to long-term partnership and iterative improvements for your business.",
  timeframe: "WEEK 9+"
}];
function ProcessStep({
  step,
  index,
  isLast
}: {
  step: (typeof steps)[number];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <div ref={ref} data-index={index} className={`relative grid grid-cols-[40px_1fr] md:grid-cols-[80px_1fr] gap-6 md:gap-12 ${isLast ? "" : "mb-20 md:mb-[120px]"} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="relative flex justify-start md:justify-center pt-8">
        <div className="relative">
          <div className={`w-3 h-3 rounded-lg transition-all duration-500 ${isVisible ? "bg-primary shadow-[0_0_20px_4px_var(--primary)] animate-pulse" : "bg-muted"}`} style={{
          boxShadow: isVisible ? "0 0 20px 4px var(--primary), 0 0 40px 8px hsla(45, 68%, 47%, 0.3)" : "none"
        }} />
        </div>
      </div>

      {}
      <div className="min-w-0">
        <div className="mb-4">
          <span className="font-mono text-xs tracking-widest text-primary">
            {step.number}
          </span>
        </div>

        <div className="relative p-6 md:p-10 backdrop-blur-sm border border-border shadow-lg rounded-lg" style={{
        background: "linear-gradient(135deg, hsla(216, 50%, 12%, 0.9) 0%, hsla(216, 40%, 20%, 0.9) 100%)"
      }}>
          <h3 className="font-default font-bold text-2xl md:text-[2.5rem] leading-[1.05] tracking-tight text-foreground mb-6">
            {step.title}
          </h3>

          <div className="w-12 h-px bg-border mb-6" />

          <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-[560px] mb-8">
            {step.description}
          </p>

          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-primary rounded-lg" />
            <span className="font-mono text-xs tracking-widest text-primary">
              {step.timeframe}
            </span>
          </div>
        </div>
      </div>
    </div>;
}
export default function HowToSubmit() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [lineProgress, setLineProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalHeight = rect.height;
      const start = windowHeight * 0.8;
      const end = -totalHeight + windowHeight * 0.5;
      const scrolled = start - rect.top;
      const range = start - end;
      const progress = Math.max(0, Math.min(1, scrolled / range));
      setLineProgress(progress);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return;
}